import React, { type FC, useMemo } from 'react';

import { useAppDispatch } from '@fd/customHooks/useAppDispatch';
import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import {
  Announcement01,
  Bank,
  BankNote02,
  BarChartSquare01,
  Bell01,
  BookClosed,
  BookOpen01,
  Building02,
  Building05,
  Building07,
  CheckVerified01,
  Code02,
  CoinsHand,
  CreditCard02,
  File06,
  Globe02,
  GoogleChrome,
  GraduationHat02,
  HelpCircle,
  HomeLine,
  Image01,
  LayoutGrid01,
  Link03,
  List,
  MessageChatCircle,
  MessageSquare01,
  MessageTextSquare01,
  Monitor01,
  Monitor02,
  Phone02,
  PhoneCall01,
  PhoneCall02,
  Plane,
  PlusCircle,
  PlusSquare,
  Receipt,
  Settings01,
  Settings02,
  Settings04,
  Square,
  Tablet01,
  Tag01,
  Tag03,
  Tool01,
  Tool02,
  Tv01,
  Users01,
  Users02,
} from '@flipdish/ui-icons';
import type {
  NavAccountData,
  NavAccountMenuItemType,
  SidebarNavigationProps,
} from '@flipdish/ui-library/components/organisms/SidebarNavigation';
import { type TranslateFunction, getActiveLanguage, getTranslate } from 'react-localize-redux';
import { add, remove } from 'react-redux-permissions';
import { checkVisibility } from 'react-redux-permissions/dist/core';
import { useHistory, useLocation } from 'react-router-dom';

import { accountActions } from '../../actions/account.actions';
import { useHasAccessToOnboardingV3 } from '../../components/OnboardingV3/hooks/useHasAccessToOnboardingV3';
import {
  propertyManagerRolePermission,
  propertyOwnerRolePermission,
} from '../../components/RMS/utils/permissions';
import { displayName } from '../../helpers/account';
import { accountRoutesConst as routes } from '../../routes/account.routes';
import { isProductBasedMenusEnabled } from '../../selectors/app.selector';
import { type ItemMetaData, menuItemsConfig } from './menuItemsConfig';

type SidebarNavItem = SidebarNavigationProps['items'][number];
type SidebarSubItem = NonNullable<SidebarNavItem['items']>[number];

/**
 * Wraps a rendered JSX element into an FC<{className?: string}> that
 * SidebarNavigation can mount with its own className for sizing/colour.
 */
const wrapIcon = (element: React.ReactElement): FC<{ className?: string }> => {
  const WrappedIcon: FC<{ className?: string }> = ({ className }) =>
    React.cloneElement(element, { className });
  return WrappedIcon;
};

export const hasAccess = (
  userPermissions: AppState['permissions'],
  itemPermissions?: AppState['permissions']
): boolean => {
  if (!itemPermissions?.length) return true;
  return checkVisibility(userPermissions, itemPermissions, []);
};

// TODO: Populate with actual @flipdish/ui-icons imports once confirmed.
// Icons are already FC<SVGProps> so they satisfy FC<{className?: string}> — no wrapIcon needed.
const topLevelIconOverrides: Record<string, FC<{ className?: string }>> = {
  Home: HomeLine,
  Properties: Building02,
  Stores: Building02,
  Sales: Receipt,
  Menu_Editor: BookOpen01,
  Menus: BookOpen01,
  Catalog: BookOpen01,
  Sales_channels: Monitor02,
  Marketing: Announcement01,
  Finance: BankNote02,
  Reports: BarChartSquare01,
  Add_ons: PlusCircle,
  First_Time_Setup: Plane,
  Settings: Settings01,
  Flipdish_Staff_Tools: Tool02,
};

const subItemIconOverrides: Record<string, FC<{ className?: string }>> = {
  // Settings sub-drawer items
  Organization: Building07,
  Brand: Tag01,
  Teammates: Users01,
  Teammate_Permissions: Users01,
  Metafields: Code02,
  Sales_channels: Monitor02,
  Sales_channel_groups: LayoutGrid01,
  Customer_messages: MessageSquare01,
  Webhooks: Link03,
  Entitlements: File06,
  Audit_logs: List,
  Bulk_Updater: Tool01,

  // Flipdish_Staff_Tools sub-items
  Bulk_Settings_Updater: Settings04,
  Advanced_Restaurant_Search: Building02,
  Advanced_User_Search: Users02,
  Org_View: Building05,
  System_settings_for_all_orgs: Settings02,
  Logo_Resizer: Image01,
  Onboarding_Configuration_Tool: Plane,
  Mobile_Apps_Tools: Phone02,
  Gloria_Food: BookOpen01,

  // Marketing sub-items
  Customers: Users01,
  Vouchers: Tag03,
  Campaigns: Announcement01,
  Push_Notifications: Bell01,
  Flipdish_Phone_Marketing: PhoneCall01,
  Flipdish_Phone_Marketing_beta: PhoneCall02,
  SMS_campaigns: MessageTextSquare01,
  Google_Listing_Management: GoogleChrome,

  // Finance sub-items
  Payouts: CoinsHand,
  Bank_accounts: Bank,
  Restaurant_vouchers: CreditCard02,
  Ledger: BookClosed,
  Verifactu: CheckVerified01,

  // Sales_channels sub-items
  Point_of_Sale: Tv01,
  Mobile_apps: Phone02,
  Website: Globe02,
  Kiosks: Monitor01,
  Phone_agent: Phone02,
  Marketplaces: Building02,

  // Catalog sub-items
  Products: BookOpen01,
  Modifier_Groups: BookOpen01,

  // Add_ons sub-items
  App_Store: PlusSquare,
  Accounts: Users01,
  Documents: Square,
  FoodSafety: Square,
  Inventory: Square,
  Payroll: Square,
  Signage: Square,
  Staff: Square,
  Central_kitchen: Square,
  Terminals: Tablet01,
};

export const transformSubItems = (
  subItems: NonNullable<ItemMetaData['subItems']>,
  permissions: AppState['permissions'],
  translate: TranslateFunction,
  search: string
): SidebarSubItem[] => {
  return subItems
    .filter((item) => !item.hidden)
    .filter((item) => hasAccess(permissions, item.permissions))
    .map((item) => ({
      label: translate(item.title) as string,
      href: item.keepQuery ? `${item.path}${search}` : item.path,
      ...(item.icon ? { icon: subItemIconOverrides[item.title] ?? wrapIcon(item.icon) } : {}),
      ...(item.isBeta ? { badge: translate('Beta') as string } : {}),
    }));
};

type ItemToggle = NonNullable<SidebarNavItem['toggle']>;

export const transformItems = (
  rawItems: ItemMetaData[],
  permissions: AppState['permissions'],
  translate: TranslateFunction,
  search: string,
  getToggle?: (item: ItemMetaData) => ItemToggle | undefined
): SidebarNavItem[] => {
  return rawItems
    .filter((item) => !item.hidden)
    .filter((item) => hasAccess(permissions, item.permissions))
    .map((item): SidebarNavItem | null => {
      const nested = item.subItems || item.subDrawerItems;
      const subItems = nested
        ? transformSubItems(nested, permissions, translate, search)
        : undefined;

      // Parent-only items (no href) with no visible children should be dropped
      if (!item.path && (!subItems || subItems.length === 0)) {
        return null;
      }

      const toggle = getToggle?.(item);

      return {
        label: translate(item.title) as string,
        ...(item.path ? { href: item.path } : {}),
        icon: topLevelIconOverrides[item.title] ?? wrapIcon(item.icon),
        ...(subItems?.length ? { items: subItems } : {}),
        ...(item.isBeta ? { badge: translate('Beta') as string } : {}),
        ...(toggle ? { toggle } : {}),
      };
    })
    .filter((item): item is SidebarNavItem => item !== null);
};

export const useSidebarNavItems = () => {
  const dispatch = useAppDispatch();
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const currentApp = useAppSelector((state) => state.currentApp);
  const account = useAppSelector((state) => state.account);
  const permissions = useAppSelector((state) => state.permissions || []);
  const isProductBasedMenusOn = useAppSelector((state) => isProductBasedMenusEnabled(state));
  const { data: rmsData } = useQueryRMSCommonDataHook(currentApp?.AppId || '');
  const subscriptions = rmsData?.subscriptions;
  const menuSource = rmsData?.associations?.menuSource;
  const location = useLocation();
  const history = useHistory();

  const handleHiddenFeatureChange = (isSelected: boolean) => {
    void dispatch(accountActions.update({ ...account, ShowHiddenFeatures: isSelected }))
      .then(() => {
        dispatch(isSelected ? add('ShowHiddenFeatures') : remove('ShowHiddenFeatures'));
      })
      .catch(() => {
        // accountActions.update already dispatches updateFailure.
      });
  };

  const activeLanguage = useAppSelector((state) => getActiveLanguage(state.locale));
  const appId = currentApp?.AppId ?? 'global';
  const { onboardingStatus, isFlipdishStaff } = useHasAccessToOnboardingV3();
  const reducedOnboardingView = onboardingStatus === 'in_progress' && !isFlipdishStaff;
  const showAppScopedLinks =
    !reducedOnboardingView && appId !== 'global' && appId !== 'flipdish-global';

  // menuItemsConfig uses hooks internally so it must be called at the top level
  const rawItems = menuItemsConfig(currentApp, isProductBasedMenusOn, menuSource, subscriptions);

  const footerTitles = ['Settings', 'Flipdish_Staff_Tools'];
  const settingsRaw = rawItems.filter((item) => footerTitles.includes(item.title));
  const nonSettingsRaw = rawItems.filter((item) => !footerTitles.includes(item.title));

  const getStaffToolsToggle = (item: ItemMetaData): ItemToggle | undefined => {
    if (item.title !== 'Flipdish_Staff_Tools') return undefined;
    return {
      label: translate('Show_hidden_features') as string,
      isSelected: !!account.ShowHiddenFeatures,
      onChange: handleHiddenFeatureChange,
    };
  };

  const items = transformItems(nonSettingsRaw, permissions, translate, location.search);
  const configFooterItems = transformItems(
    settingsRaw,
    permissions,
    translate,
    location.search,
    getStaffToolsToggle
  );

  const supportedLanguages = ['en', 'fr', 'de', 'es', 'nl'];
  const langCode = supportedLanguages.includes(activeLanguage) ? activeLanguage : 'en';

  const supportItem: SidebarNavItem = {
    label: translate('Support') as string,
    icon: HelpCircle,
    items: [
      {
        label: translate('Support_articles') as string,
        href: `https://help.flipdish.com/${langCode}`,
        icon: Globe02,
      },
      {
        label: translate('Talk_to_us') as string,
        href: 'https://help.flipdish.com/s/contactsupport?language=en_GB',
        icon: MessageChatCircle,
      },
      {
        label: translate('Flipdish_Academy') as string,
        href: 'https://academy.flipdish.com/',
        icon: GraduationHat02,
      },
    ],
  };

  const footerItems: SidebarNavItem[] = [
    ...(!reducedOnboardingView ? [supportItem] : []),
    ...configFooterItems,
  ];

  const subscriptionPermissions: AppState['permissions'] = [
    'Owner',
    ...propertyManagerRolePermission,
    ...propertyOwnerRolePermission,
    'ManagedOwner',
    'FinanceManger',
    'FlipdishStaff',
  ];
  const hasSubscriptionAccess =
    showAppScopedLinks && hasAccess(permissions, subscriptionPermissions);

  const accountData: NavAccountData = useMemo(() => {
    const menuItems: NavAccountMenuItemType[] = [
      {
        label: translate('My_account') as string,
        onClick: () => history.push(`/${appId}/profile`),
      },
    ];

    if (hasSubscriptionAccess) {
      menuItems.push(
        {
          label: translate('Subscriptions') as string,
          onClick: () => history.push(`/${appId}/billing/org-subscriptions/subscriptions`),
        },
        {
          label: translate('Invoices') as string,
          onClick: () => history.push(`/${appId}/finance/invoices`),
        }
      );
    }

    if (!reducedOnboardingView) {
      menuItems.push({
        label: translate('developer_tools') as string,
        onClick: () => history.push(`/${appId}/developers`),
      });
    }

    return {
      account: {
        id: String(account?.AccountId ?? ''),
        name: displayName(account),
        email: account?.Email || '',
        avatar: '',
        status: 'online' as const,
      },
      menuItems,
      bottomMenuItems: [
        {
          label: translate('Logout') as string,
          onClick: () => history.push(routes.Logout),
        },
      ],
    };
  }, [account, translate, appId, history, hasSubscriptionAccess, reducedOnboardingView]);

  return {
    items,
    footerItems,
    account: accountData,
    activeUrl: location.pathname,
  };
};
